import useCreateRecordDialog from 'hooks/useCreateRecordDialog';
import { useCreateTeamMutation } from 'store/slices/apiV1/properties';
import {
  HookFormInput,
  HookFormSelect,
  HookFormCheckbox,
} from 'ui-component/HookFormComponents';
import {
  createUpdateTeamFormConstants,
  createUpdateTeamValidationSchema,
} from 'views/properties/Teams/components/constants';
import { HookFormTeamTypeAutocomplete } from 'ui-component/HookFormComponents/HookFormTeamTypeAutocomplete';
import HookFormStockLocationSelect from 'ui-component/HookFormComponents/HookFormStockLocationSelect';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { useSelector } from 'store';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import { Org } from 'types/org';
import {
  getDefaultShippingMethod,
  getShipmentCarrierAndService,
} from 'utils/functions';
import { ShippingMethods } from 'types/purchasing';
import { ListSubheader, MenuItem, Stack } from '@mui/material';
import { cofactrSlug } from 'constants/appConstants';
import { StockLocationType } from 'types/inventory';
import _ from 'lodash';
import FormSection from 'ui-component/FormSection';

type CreateTeamDialogProps = {
  dialogOpen: boolean;
  onClose: () => void;
  onCreateSuccess?: (record: object) => void;
  overrideOrgId?: Org['id'];
};

const CreateTeamDialog = ({
  dialogOpen,
  onClose,
  onCreateSuccess,
  overrideOrgId,
}: CreateTeamDialogProps) => {
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(
    overrideOrgId ?? activeOrgId ?? skipToken
  );
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const formDefaults = {
    [createUpdateTeamFormConstants.name.id]: '',
    [createUpdateTeamFormConstants.teamType.id]: org?.defaultTeamType?.id ?? '',
    [createUpdateTeamFormConstants.industry.id]: '',
    [createUpdateTeamFormConstants.militaryUse.id]: false,
    [createUpdateTeamFormConstants.dpas.id]: '',
    [createUpdateTeamFormConstants.defaultShippingMethod.id]:
      getDefaultShippingMethod({ org }) ?? '',
    [createUpdateTeamFormConstants.defaultCofactrWarehouse.id]: '',
    [createUpdateTeamFormConstants.defaultShippingDestinationLocation.id]: '',
  };

  const preSubmit = (data: Record<string, unknown>) => {
    const payload = { ...data };
    const method = _.get(
      payload,
      createUpdateTeamFormConstants.defaultShippingMethod.id
    ) as string | undefined;
    if (method) {
      const { defaultShippingCarrier, defaultShippingService } =
        getShipmentCarrierAndService(method);
      _.set(payload, 'defaultShippingCarrier', defaultShippingCarrier);
      _.set(payload, 'defaultShippingService', defaultShippingService);
    }
    _.unset(payload, createUpdateTeamFormConstants.defaultShippingMethod.id);
    const warehouse = _.get(
      payload,
      createUpdateTeamFormConstants.defaultCofactrWarehouse.id
    );
    const destination = _.get(
      payload,
      createUpdateTeamFormConstants.defaultShippingDestinationLocation.id
    );
    _.set(
      payload,
      createUpdateTeamFormConstants.defaultCofactrWarehouse.id,
      warehouse === '' ? null : warehouse
    );
    _.set(
      payload,
      createUpdateTeamFormConstants.defaultShippingDestinationLocation.id,
      destination === '' ? null : destination
    );
    return payload;
  };

  const { methods, control, errors, BaseCreateRecordDialog, dialogProps } =
    useCreateRecordDialog({
      formConstants: createUpdateTeamFormConstants,
      validationSchema: createUpdateTeamValidationSchema(
        handleReplaceWithCustomTeamName
      ),
      newRecordDefaults: formDefaults,
      useCreateMutation: useCreateTeamMutation,
      recordName: handleReplaceWithCustomTeamName('Team'),
      submitAsPayload: true,
      preSubmit,
      onSave: onCreateSuccess,
      otherProps: overrideOrgId
        ? {
            overrideOrgId,
          }
        : undefined,
    });

  return (
    <BaseCreateRecordDialog
      open={dialogOpen}
      onClose={onClose}
      dialogProps={dialogProps}
    >
      <Stack spacing={2}>
        <HookFormInput
          name={createUpdateTeamFormConstants.name.id}
          label={handleReplaceWithCustomTeamName(
            createUpdateTeamFormConstants.name.label
          )}
          control={control}
          errors={errors}
        />
        <HookFormTeamTypeAutocomplete
          name={createUpdateTeamFormConstants.teamType.id}
          label={handleReplaceWithCustomTeamName(
            createUpdateTeamFormConstants.teamType.label
          )}
          control={control}
          errors={errors}
          defaultValue={
            formDefaults[createUpdateTeamFormConstants.teamType.id] as
              | string
              | undefined
          }
          setValue={methods.setValue}
          overrideOrgId={overrideOrgId}
        />
        <FormSection title="Optional Settings to Override Org Defaults">
          <Stack spacing={2}>
            <HookFormInput
              name={createUpdateTeamFormConstants.industry.id}
              label={createUpdateTeamFormConstants.industry.label}
              control={control}
              errors={errors}
            />
            <HookFormCheckbox
              name={createUpdateTeamFormConstants.militaryUse.id}
              label={createUpdateTeamFormConstants.militaryUse.label}
              control={control}
              errors={errors}
            />
            <HookFormInput
              name={createUpdateTeamFormConstants.dpas.id}
              label={createUpdateTeamFormConstants.dpas.label}
              control={control}
              errors={errors}
            />
            <HookFormSelect
              name={createUpdateTeamFormConstants.defaultShippingMethod.id}
              label={createUpdateTeamFormConstants.defaultShippingMethod.label}
              control={control}
              errors={errors}
            >
              {ShippingMethods.map((method) =>
                method.type === 'option' ? (
                  <MenuItem value={method.value} key={method.value}>
                    {method.label}
                  </MenuItem>
                ) : (
                  <ListSubheader key={method.value}>
                    {method.label}
                  </ListSubheader>
                )
              )}
            </HookFormSelect>
            <HookFormStockLocationSelect
              name={createUpdateTeamFormConstants.defaultCofactrWarehouse.id}
              label={
                createUpdateTeamFormConstants.defaultCofactrWarehouse.label
              }
              control={control}
              errors={errors}
              filters={[
                {
                  field: 'locationType',
                  operator: '=',
                  value: StockLocationType.FACILITY,
                },
                {
                  // @ts-ignore because query filter typing does not support nested filters like this
                  field: 'org.slug',
                  operator: '=',
                  value: cofactrSlug,
                },
                {
                  field: 'warehouseShippingEnabled',
                  operator: '=',
                  value: true,
                },
              ]}
              mb={0}
            />
            <HookFormStockLocationSelect
              name={
                createUpdateTeamFormConstants.defaultShippingDestinationLocation
                  .id
              }
              label={
                createUpdateTeamFormConstants.defaultShippingDestinationLocation
                  .label
              }
              control={control}
              errors={errors}
              setValue={methods.setValue}
              contextualCreate
              limitCreateLocationTypeToFacility
              filters={[
                {
                  field: 'locationType',
                  operator: '=',
                  value: StockLocationType.FACILITY,
                },
              ]}
              mb={0}
            />
          </Stack>
        </FormSection>
      </Stack>
    </BaseCreateRecordDialog>
  );
};

export default CreateTeamDialog;
